import React, { useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Languages as LanguagesIcon } from 'lucide-react';
import { canonicalizeCvLanguageName } from '@tabbio/utils/cv-languages';
import { normalizeCvLanguageProficiency } from '@tabbio/utils/cv-language-proficiency';
import { useToast } from '../../../hooks/useToast';
import { useCVData } from '../../../lib/cv-mock-data';
import { parseLanguageString } from '../../../lib/cv-mapping';
import {
  WebInput,
  WebSaveButton,
  WebAddButton,
  WebFormDialog,
  WebItemCard,
  WebEmptyState,
} from './web-form-elements';
import { validateLanguageEntry } from './cv-form-validation';
import { useHydratedArrayState } from './useHydratedFormState';
import { confirmDeleteCvItem } from './delete-confirmation';
import { findCvLanguageSuggestions } from './language-suggestions';
import {
  LANGUAGE_PROFICIENCY_OPTIONS,
  languageProficiencyDisplayLabel,
} from './language-proficiency';
import { cvChangeNotApplied } from '../../../lib/cv-write-coordinator';

// `proficiency` holds the canonical code (B12); the translated label is only
// rendered, never stored, so an Arabic-UI save keeps its level.
interface LangDraft { name: string; proficiency: string }
function emptyDraft(): LangDraft { return { name: '', proficiency: '' }; }
function composeLanguage(d: LangDraft): string {
  const name = canonicalizeCvLanguageName(d.name);
  const prof = normalizeCvLanguageProficiency(d.proficiency) || d.proficiency.trim();
  if (!name) return '';
  return prof ? `${name} (${prof})` : name;
}

const ERROR_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--tabbio-danger, #e53e3e)',
  marginTop: 4,
  display: 'block',
};

interface EditLanguagesFormProps { onSave?: (languages: string[]) => Promise<void> | void; closeOnSave?: boolean; ctaLabel?: string; }

export function EditLanguagesForm({ onSave, closeOnSave = true, ctaLabel }: EditLanguagesFormProps) {
  const { t } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const [items, setItems] = useHydratedArrayState(profile.languages);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<LangDraft>(emptyDraft());
  const [showErrors, setShowErrors] = useState(false);

  const existingLanguageNames = items
    .filter((_, idx) => idx !== editingIndex)
    .map((item) => parseLanguageString(item).language)
    .filter((value): value is string => Boolean(value));
  const nameError = draft.name.trim()
    ? validateLanguageEntry(draft.name.trim(), existingLanguageNames, t) ?? ''
    : t('cv.edit.languageRequired');
  const profError = !draft.proficiency.trim() ? t('cv.edit.proficiencyRequired') : '';
  const hasErrors = !!nameError || !!profError;
  const languageQuery = draft.name.trim().toLowerCase();
  const languageSuggestions = findCvLanguageSuggestions(
    languageQuery,
    existingLanguageNames,
    8,
  );

  const openEdit = (i: number) => {
    const parsed = parseLanguageString(items[i]);
    setDraft({ name: parsed.language, proficiency: parsed.proficiency });
    setEditingIndex(i);
    setShowErrors(false);
  };
  const openAdd = () => { setDraft(emptyDraft()); setEditingIndex(-1); setShowErrors(false); };

  const handleDialogSave = () => {
    if (hasErrors) { setShowErrors(true); return; }
    const value = composeLanguage(draft);
    if (!value) { setEditingIndex(null); return; }
    if (editingIndex === -1) setItems([...items, value]);
    else if (editingIndex !== null) { const next = [...items]; next[editingIndex] = value; setItems(next); }
    setEditingIndex(null);
    setShowErrors(false);
  };

  const handleCardDelete = (i: number) => {
    void confirmDeleteCvItem(t, 'language', () => setItems(items.filter((_, idx) => idx !== i)));
  };

  const handleSave = async () => {
    if (isSaving) return;
    updateProfile({ languages: items });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave(items);
        toast.success(t('cv.edit.saved'));
        if (closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere; "keep editing" keeps this draft.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === 'discarded' && closeOnSave && router.canGoBack()) router.back();
          return;
        }
        toast.error(t('cv.edit.saveFailed'));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((lang, i) => {
        const parsed = parseLanguageString(lang);
        return (
          <WebItemCard
            key={`${lang}-${i}`}
            title={parsed.language}
            subtitle={languageProficiencyDisplayLabel(parsed.proficiency, (key) => t(key))}
            icon={<LanguagesIcon size={18} color="var(--text-secondary)" strokeWidth={1.75} />}
            onClick={() => openEdit(i)}
            onDelete={() => handleCardDelete(i)}
          />
        );
      })}
      {items.length === 0 && (
        <WebEmptyState
          icon={<LanguagesIcon size={32} color="var(--text-tertiary)" strokeWidth={1.25} />}
          text={t('cv.edit.addLanguageHint')}
        />
      )}
      <WebAddButton label={t('cv.edit.addLanguage')} onClick={openAdd} />
      <WebSaveButton label={ctaLabel ?? t('cv.edit.save')} onClick={handleSave} loading={isSaving} disabled={isSaving} />

      <WebFormDialog
        visible={editingIndex !== null}
        title={editingIndex === -1 ? t('cv.edit.addLanguage') : t('cv.edit.editLanguage')}
        onClose={() => setEditingIndex(null)}
      >
        <WebInput
          label={t('cv.edit.language')}
          value={draft.name}
          onChange={(v) => setDraft({ ...draft, name: v })}
          placeholder={t('cv.edit.language')}
        />
        {showErrors && nameError ? <span style={ERROR_STYLE}>{nameError}</span> : null}
        {languageSuggestions.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {languageSuggestions.map((language) => (
              <button
                key={language}
                type="button"
                onClick={() => setDraft({ ...draft, name: language })}
                style={{
                  padding: '7px 12px',
                  borderRadius: 9999,
                  border: '1px solid var(--input-border)',
                  background: 'var(--bg)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                }}
              >
                {language}
              </button>
            ))}
          </div>
        ) : null}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
            {t('cv.edit.proficiency')}
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {LANGUAGE_PROFICIENCY_OPTIONS.map((option) => {
              const label = t(`cv.edit.proficiencyLevels.${option.key}`);
              const active = draft.proficiency === option.code;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setDraft({ ...draft, proficiency: option.code })}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 9999,
                    border: active ? '1px solid var(--text-primary)' : '1px solid var(--input-border)',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    backgroundColor: active ? 'var(--text-primary)' : 'var(--bg)',
                    color: active ? 'var(--bg)' : 'var(--text-primary)',
                    transition: 'background-color 150ms, border-color 150ms, color 150ms',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        {showErrors && profError ? <span style={ERROR_STYLE}>{profError}</span> : null}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            onClick={handleDialogSave}
            disabled={showErrors && hasErrors}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 9999,
              backgroundColor: 'var(--text-primary)',
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
              border: 'none',
              cursor: showErrors && hasErrors ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: showErrors && hasErrors ? 0.5 : 1,
            }}
          >
            {editingIndex === -1 ? t('cv.edit.add') : t('cv.edit.save')}
          </button>
        </div>
      </WebFormDialog>
    </div>
  );
}
